/**
 * The day a JPEG says it was taken (`2026:10:12 10:30:00` in its EXIF), as `2026-10-12`, or null
 * when the file says nothing (a PNG, a WebP, a photo with the data stripped). It reads the first
 * 128 KB and nothing else; a wrong guess is never made.
 */
export function exifTakenOn(bytes: Uint8Array): string | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (bytes.length < 12 || view.getUint16(0) !== 0xffd8) return null;
  let offset = 2;
  while (offset + 4 < bytes.length) {
    if (view.getUint8(offset) !== 0xff) return null;
    const marker = view.getUint8(offset + 1);
    const size = view.getUint16(offset + 2);
    if (marker === 0xe1 && readAscii(bytes, offset + 4, 4) === 'Exif') {
      return fromTiff(view, offset + 10, Math.min(offset + 2 + size, bytes.length));
    }
    if (marker === 0xda || size < 2) return null;
    offset += 2 + size;
  }
  return null;
}

function readAscii(bytes: Uint8Array, from: number, length: number): string {
  let out = '';
  for (let i = from; i < from + length && i < bytes.length; i += 1) {
    out += String.fromCharCode(bytes[i] as number);
  }
  return out;
}

function fromTiff(view: DataView, base: number, end: number): string | null {
  if (base + 8 > end) return null;
  const little = view.getUint16(base) === 0x4949;
  const u16 = (at: number) => view.getUint16(at, little);
  const u32 = (at: number) => view.getUint32(at, little);
  const bytes = new Uint8Array(view.buffer, view.byteOffset, view.byteLength);

  const findIn = (ifd: number, wanted: number[]): { tag: number; at: number } | null => {
    if (ifd < 0 || base + ifd + 2 > end) return null;
    const count = u16(base + ifd);
    for (let i = 0; i < count; i += 1) {
      const entry = base + ifd + 2 + i * 12;
      if (entry + 12 > end) return null;
      const tag = u16(entry);
      if (wanted.includes(tag)) return { tag, at: entry };
    }
    return null;
  };
  const text = (entry: number): string | null => {
    const where = base + u32(entry + 8);
    if (where + 19 > end) return null;
    return readAscii(bytes, where, 19);
  };
  const parse = (value: string | null): string | null => {
    const found = value === null ? null : /^(\d{4}):(\d{2}):(\d{2})/.exec(value);
    if (found === null || found[1] === '0000') return null;
    return `${found[1]}-${found[2]}-${found[3]}`;
  };

  const first = u32(base + 4);
  const sub = findIn(first, [0x8769]);
  if (sub !== null) {
    const original = findIn(u32(sub.at + 8), [0x9003, 0x9004]);
    const day = original === null ? null : parse(text(original.at));
    if (day !== null) return day;
  }
  const modified = findIn(first, [0x0132]);
  return modified === null ? null : parse(text(modified.at));
}

/** The day to send with a photo: from the file when it says, else nothing (the API uses the upload day). */
export async function photoTakenOn(file: File): Promise<string | null> {
  if (file.type !== 'image/jpeg') return null;
  try {
    return exifTakenOn(new Uint8Array(await file.slice(0, 131_072).arrayBuffer()));
  } catch {
    return null;
  }
}
